<script lang="ts" setup>
import {formatInstanteToTimeBR, formatUsDateToBr} from "~/utils/datetime";
import type {Ticket} from "~/types/ticket";
import {useTrackingMkt} from "~/composables/useTrackingMkt";
import type {Event} from "~/types/event";

const route = useRoute();
const {isMobile} = useWindowSize()
const {findEventBySlug} = useEvent()
const {producerGa4Id, producerPixelId, findProducerConfigEvent, trackingEvent} = useTrackingMkt()

const slug = ref(route.params?.slug || '')
const {data: event} = await useAsyncData<Event>(
    () => `find-event-${slug.value}`,
    () => findEventBySlug(slug.value)
)

watch(() => event.value?.id, () => {
    const aff = route.query?.aff
  if (event.value?.id) {
    useAsyncData(`producer-config-event-${slug}`, () => findProducerConfigEvent(event.value.id))

    if (aff) {
      const affCookie = useCookie('aff_' + event.value.id, {maxAge: 60 * 60 * 24 * 30, path: '/'})
      affCookie.value = aff as string
    }
  }
}, {immediate: true})


const tag = computed((): string => `${slug}-${event.value?.subCategory?.category?.slug}`
    .replace(/<[^>]*>/g, '')
    .replace('-', ', '))

const description = computed((): string => {
  return `Ingressos para ${event.value?.name} em ${event.value?.eventAddress?.city} - ${event.value?.eventAddress?.state}`
})

const normalizedDescription = computed(() => {
  if (!event.value?.description) return ''
  let html = event.value.description
      .replace(/>\s+</g, '><')
      .replace(/\s+/g, ' ')
      .trim()
  html = html.replace(/<([^>]+)\/>/g, '<$1></$1>')
  html = html.replace(/[\n\r]+/g, '')
  return html
})

useHead({
  title: 'Seu Rolê|' + event.value?.name,
  meta: [
    {name: 'description', content: description.value},
    {name: 'keywords', content: tag.value},
    {property: 'og:title', content: event.value?.name},
    {property: 'og:description', content: description.value},
    {property: 'og:image', content: event.value?.logo},
  ],
  script: [
    {
      children: `
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              'producer_ga4_id': '${producerGa4Id.value || ''}',
              'producer_pixel_id': '${producerPixelId.value || ''}',
              'event': 'producer_config'
            });
          `,
      type: 'text/javascript',
      tagPriority: 'critical'
    }
  ]
})

const breadcrumbData = [{
  title: 'Home',
  link: '/',
  subitems: [
    {title: 'Eventos', link: '/eventos'},
    {title: event.value?.name}
  ]
}]

onMounted(() => {
  if (event.value) {
    const {sendEvent} = useTrackingMkt()
    let price: number | undefined
    try {
      price = (event.value?.tickets || []).sort((a: Ticket, b: Ticket) => a?.amount - b?.amount)[0]?.amount
    } catch (e) {
      price = 0
    }
    if (price && event.value?.id) {
      sendEvent({
        event: 'view_item',
        items: [{
          item_name: event.value.name,
          item_id: event.value.id,
          price,
          quantity: 1
        }]
      })
      trackingEvent(event.value.id)
    }
  }
})

</script>

<template>
  <div class="container py-2">
    <Breadcrumb :data="breadcrumbData"/>

    <div class="row">
      <div class="col-md-8">
        <div class="d-flex flex-column">
          <div class="order-lg-1 order-2 mb-2">
            <div class="card shadow-sm card-hover border-0 h-100">
              <div class="card-img-top card-img-hover">
                <div class="content-overlay end-0 top-0 pt-3 pe-3">
                  <FavoriteButton v-if="event?.id" :event-id="event.id"/>
                  <ShareButton v-if="event?.id" :event-id="event.id"/>
                </div>
                <img :src="event?.logo" :alt="event?.name" class="img-em-alta rounded-3 mt-3 mt-md-0"/>
              </div>
            </div>
          </div>
          <div class="order-lg-2 order-1">
            <h1 class="h2 mb-2">{{ event?.name }}</h1>
            <p class="mb-0 pb-1 ">
              <Icon icon="fluent-mdl2:date-time-mirrored" class="text-dark"/>
              {{ formatUsDateToBr(event?.startAt) }} início às {{ formatInstanteToTimeBR(event?.startAt) }}
            </p>
            <p class="mb-0">
              <Icon icon="fluent-mdl2:date-time-mirrored"/>
              {{ fullAddress(event?.eventAddress) }}
            </p>
          </div>
        </div>
        <div>
          <EventTicketSell :event="event" v-if="isMobile" class="pt-2"/>
          <h5 class="pt-4 mt-3">Descrição do evento</h5>
          <p class=" opacity-70 mb-1">
            <RenderHtml :content="event?.description"/>
          </p>
          <hr>
          <template v-if="event?.producerPageInfo">
            <h5 class="pt-4 mt-3">Sobre o produtor</h5>
            <div class="d-flex align-items-start justify-content-between">
              <div class="d-flex align-items-start">
                <div class="position-relative flex-shrink-0">
                  <img class="rounded-circle" :src="event.producerPageInfo.imageLogo" style="width: 100px;height: 100px"
                       alt="Annette Black">
                </div>
                <div class="ps-3 ps-sm-4">
                  <a :href="event?.producerPageInfo.urlSite" target="_blank" class="nav-link h5 mb-1">
                    {{ event?.producerPageInfo?.organizerName }}
                  </a>
                  <ul class="list-unstyled fs-sm mb-0">
                    <li class="d-flex text-nav text-break">
                      <i class="fi-mail opacity-60 mt-1 me-2"></i>
                      <a :href="`mailto:${event?.producerPageInfo?.contactEmail}`">
                        {{ event?.producerPageInfo?.contactEmail }}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <p class="small">
              <span>{{ event?.producerPageInfo?.description }}</span>
            </p>
          </template>
        </div>
      </div>
      <!-- Sidebar-->
      <EventTicketSell :event="event" v-if="!isMobile" class="pt-5"/>
    </div>
  </div>

</template>
<style>
.icon-size {
  font-size: 22px;
}
</style>
